import { blendColor } from '../../../lib/color/blend';

type OrgChartThemeMode = 'light' | 'dark';

interface OrgFilterDimPaletteInput {
  mode?: OrgChartThemeMode;
  opacity: number;
  backgroundColor?: string;
  fillColor?: string;
  strokeColor?: string;
  primaryTextColor?: string;
  secondaryTextColor?: string;
  detailLabelColor?: string;
  detailValueColor?: string;
  accentColor?: string;
}

interface OrgFilterDimPalette {
  isDimmed: boolean;
  fillColor?: string;
  strokeColor?: string;
  primaryTextColor?: string;
  secondaryTextColor?: string;
  detailLabelColor?: string;
  detailValueColor?: string;
  accentColor?: string;
  separatorColor: string;
  imageOpacity: number;
  iconOpacity: number;
}

const LIGHT_DIM_TOKENS = {
  surface: '#F3F6FA',
  stroke: '#CBD5E1',
  primaryText: '#64748B',
  secondaryText: '#7A8899',
  accent: '#B6C2CF',
  separator: '#DCE5ED',
};

const DARK_DIM_TOKENS = {
  surface: '#314155',
  stroke: '#617287',
  primaryText: '#C3CFDD',
  secondaryText: '#AAB7C7',
  accent: '#8496AA',
  separator: '#4B5C70',
};

const clamp = (value: number, min = 0, max = 1) => Math.max(min, Math.min(max, value));

export const getOrgFilterDimPalette = ({
  mode = 'light',
  opacity,
  backgroundColor,
  fillColor,
  strokeColor,
  primaryTextColor,
  secondaryTextColor,
  detailLabelColor,
  detailValueColor,
  accentColor,
}: OrgFilterDimPaletteInput): OrgFilterDimPalette => {
  const isDimmed = opacity > 0 && opacity < 1;
  const theme = mode === 'dark' ? DARK_DIM_TOKENS : LIGHT_DIM_TOKENS;
  const blendTarget = backgroundColor || (mode === 'dark' ? '#0B1020' : '#FFFFFF');

  if (!isDimmed) {
    return {
      isDimmed: false,
      fillColor,
      strokeColor,
      primaryTextColor,
      secondaryTextColor,
      detailLabelColor,
      detailValueColor,
      accentColor,
      separatorColor: theme.separator,
      imageOpacity: 1,
      iconOpacity: 1,
    };
  }

  const dimAmount = clamp(1 - opacity, 0, 0.92);

  return {
    isDimmed: true,
    fillColor: blendColor(fillColor || theme.surface, blendTarget, dimAmount, blendTarget),
    strokeColor: blendColor(strokeColor || theme.stroke, blendTarget, dimAmount, blendTarget),
    primaryTextColor: blendColor(primaryTextColor || theme.primaryText, blendTarget, dimAmount, blendTarget),
    secondaryTextColor: blendColor(secondaryTextColor || theme.secondaryText, blendTarget, dimAmount, blendTarget),
    detailLabelColor: blendColor(detailLabelColor || theme.secondaryText, blendTarget, dimAmount, blendTarget),
    detailValueColor: blendColor(detailValueColor || theme.primaryText, blendTarget, dimAmount, blendTarget),
    accentColor: blendColor(accentColor || theme.accent, blendTarget, dimAmount, blendTarget),
    separatorColor: blendColor('#F3F4F6', blendTarget, dimAmount, blendTarget),
    imageOpacity: clamp(opacity, 0.3, 1),
    iconOpacity: clamp(opacity, 0.3, 1),
  };
};
